



















































































import {Component, Prop, Watch} from 'vue-property-decorator';
import {ProductDto} from '@eurogast/hub-client-typescript';
import {LockService} from '@/services/LockService';
import UnitSelector from '@/common/UnitSelector.vue';
import AmountSelector from '@/common/AmountSelector.vue';
import ProductBadges from '@/search-result/ProductBadges.vue';
import {namespace} from 'vuex-class';
import {CART} from '@/store/types';
import {ApiService} from '@/services/ApiService';
import ProductDtoComponentBase from '@/common/ProductDtoComponentBase';
import {Cart, CartItem} from '@/services/cart/model/cart';
import {CartEvents} from '@/services/cart/model/cart-events';
import {DependencyService} from '@/services/DependencyService';
import Nara from '@/domain/product/Nara';
import MatomoEvents from '@/matomo/events/MatomoEvents';
import MatomoSearch from '@/matomo/MatomoSearch';
import AddToCartButton from '@/common/cart-button/AddToCartButton.vue';
import CartButtonMessage from '@/common/cart-button/CartButtonMessage.vue';
import { CONVI_KEY } from '@/services/product/ProductService';

const cart = namespace(CART);

@Component({
  components: {CartButtonMessage, AddToCartButton, ProductBadges, AmountSelector, UnitSelector}
})
export default class AutoCompleteProduct extends ProductDtoComponentBase {
  get minSaleQuantity() {
    return (this.product.minSaleQuantity / this.currentUnit.ratio) || 0;
  }

  get isConvenienceProduct() {
    return this.product.virtualCategories.some((virtualCategory) => virtualCategory === CONVI_KEY);
  }

  get cartItem(): CartItem {
    const unit = this.currentUnit;
    const item = CartItem.buildWithUnit(
        this.product.sku,
        this.currentAmount,
        unit
    );
    item.product = this.product;
    return item;
  }

  get availableNaras(): Nara[] {
    return Nara.findAvailableNaras(this.naraCodes);
  }

  get isPurchasable(): boolean {
    return this.product.availability === ProductDto.AvailabilityEnum.ACTIVE;
  }

  get showSelectors(): boolean {
    return this.isLoggedIn && this.isPurchasable;
  }

  @Prop()
  public product: ProductDto;

  @Prop({required: false, default: () => []})
  public naraCodes: string[];

  public currentUnit = this.product.units.find((u) => u.isDefault);
  public currentAmount = Math.max(this.product.minSaleQuantity, 1);
  public unitHasChanged = false;
  public amountHasChanged = false;
  public inCart = false;

  @cart.State('cart')
  public cart: Cart;

  private cartService = DependencyService.getCartService();

  private unitWatcher;
  private amountWatcher;
  private lockService: LockService;
  private deletePending = false;
  private currentAmountString = Math.max(this.product.minSaleQuantity, 1).toString();

  public onAmountChange(newValue, oldValue) {
    this.putItemIfChanged(newValue, oldValue);
    if (!this.amountHasChanged && this.inCart) {
      this.amountHasChanged = true;
      setTimeout(() => {
        this.amountHasChanged = false;
      }, 3000);
    }
  }

  public onUnitChange(newValue, oldValue) {
    MatomoSearch.trackSearchResultInteractionAsSearchEvent();
    MatomoEvents.forSearch.unitChange.publishWithCartInformation(this.inCart);
    if (this.currentAmount * newValue.ratio < this.product.minSaleQuantity) {
      this.currentAmount = Math.max(Math.ceil(this.product.minSaleQuantity / newValue.ratio), 1);
    }
    this.putItemIfChanged(newValue, oldValue);
    if (!this.unitHasChanged && this.inCart) {
      this.unitHasChanged = true;
      setTimeout(() => {
        this.unitHasChanged = false;
      }, 3000);
    }
  }

  public addToCart() {
    MatomoSearch.trackSearchResultInteractionAsSearchEvent();
    MatomoEvents.forSearch.addItem.publish();
    if (!this.isValidAmount(this.currentAmountString)) {
      const minQuantity = Math.max(Math.ceil(this.minSaleQuantity), 1);
      ApiService.defaultInfoHandler(this.$t('searchForm.searchAutoCompletionProduct.invalidAmountForProduct',
          {sku: this.product.sku, productName: this.product.name, minQty: minQuantity}).toString()
      );
      return;
    }
    this.cartService.putItem(this.cartItem);
  }

  public removeFromCart() {
    MatomoSearch.trackSearchResultInteractionAsSearchEvent();
    if (this.inCart && !this.deletePending) {
      MatomoEvents.forSearch.removeItem.publish();
      this.deletePending = true;
      this.cartService.removeItem(this.cartItem);
    }
  }

  public mounted() {
    if (!this.currentUnit) {
      this.currentUnit = this.product.units[0];
    }
    this.lockService = new LockService(this.beforeUnload.bind(this), 1000);
    this.onCartChange();
    if (this.inCart) {
      const cartItem = this.cartService.getItem(this.product.sku);
      this.currentUnit = this.product.units.find((u) => u.abbreviation === cartItem.unitAbbreviation);
      this.currentAmount = cartItem.amount;
    } else {
      this.currentAmount = Math.max(Math.ceil(this.product.minSaleQuantity / this.currentUnit.ratio), 1);
    }
    this.$events.on(CartEvents.CART_UPDATE, this.onCartChange);
  }

  public beforeUnload() {
    this.cartService.putItem(this.cartItem, true);
  }

  @Watch('cart')
  public onCartChange() {
    if (!this.cart) {
      return;
    }

    if (this.unitWatcher) {
      this.unitWatcher();
    }
    if (this.amountWatcher) {
      this.amountWatcher();
    }

    const existingItem = this.cartService.getItem(this.product.sku);
    if (existingItem) {
      this.currentAmount = existingItem.amount;
      this.currentUnit = this.product.units.find((unit) => unit.abbreviation === existingItem.unitAbbreviation);
      this.inCart = true; // set last to avoid change handlers saving the item
      this.unitWatcher = this.$watch('currentUnit', this.onUnitChange);
      this.amountWatcher = this.$watch('currentAmount', this.onAmountChange);
    } else {
      this.deletePending = false;
      this.inCart = false;
    }
  }

  private putItemIfChanged(newValue, oldValue) {
    if (newValue !== oldValue && this.inCart) {
      this.cartService.putItemWithLock(this.cartItem, this.lockService);
    }
  }

  private setCurrentAmountString(argument) {
    MatomoSearch.trackSearchResultInteractionAsSearchEvent();
    MatomoEvents.forSearch.amountChange.publishWithCartInformation(this.inCart);
    this.currentAmountString = argument;
    if (this.isValidAmount(argument)) {
      this.currentAmount = argument;
    }
  }
}
